<template>
  <div class="home-view">
    <div class="content-container">
      <!-- Logo / Titel -->
      <div class="hero-section">
        <h1 class="app-title">🎵 SoundBattle</h1>
        <p class="app-subtitle">Das ultimative Musik-Quiz-Spiel</p>
      </div>

      <!-- Status Badge -->
      <div class="status-section">
        <SpotifyStatusBadge />
      </div>

      <!-- Action Buttons -->
      <div class="actions-section">
        <!-- Spotify Connect -->
        <SpotifyConnectButton
          v-if="!isAuthenticated"
          size="lg"
          full-width
        />

        <!-- Ohne Spotify fortfahren -->
        <PrimaryButton
          v-if="!isAuthenticated"
          variant="ghost"
          size="lg"
          full-width
          @click="continueWithoutSpotify"
        >
          Ohne Spotify fortfahren (nur 30 Sek. Vorschau)
        </PrimaryButton>

        <!-- Spiel starten -->
        <PrimaryButton
          size="lg"
          full-width
          @click="startGame"
        >
          Spiel starten
        </PrimaryButton>

        <!-- Logout Button (wenn verbunden) -->
        <PrimaryButton
          v-if="isAuthenticated"
          variant="secondary"
          size="md"
          full-width
          @click="handleLogout"
        >
          Von Spotify trennen
        </PrimaryButton>
      </div>

      <!-- Info Box -->
      <div class="info-box">
        <h3>Wie funktioniert SoundBattle?</h3>
        <ol>
          <li>Scanne den QR-Code auf deiner Spielkarte</li>
          <li>Drücke den Play-Button</li>
          <li>Drehe dein Handy mit dem Display nach unten</li>
          <li>Der Song startet automatisch!</li>
        </ol>
        <p class="info-note">
          Mit Spotify Premium kannst du komplette Songs hören. 
          Ohne Premium gibt's 30-Sekunden-Vorschauen.
        </p>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useRouter } from 'vue-router';
import { useSpotifyAuth } from '@/composables/useSpotifyAuth';
import { useGameState } from '@/composables/useGameState';
import SpotifyConnectButton from '@/components/Spotify/SpotifyConnectButton.vue';
import SpotifyStatusBadge from '@/components/Spotify/SpotifyStatusBadge.vue';
import PrimaryButton from '@/components/UI/PrimaryButton.vue';

const router = useRouter();
const { isAuthenticated, isPremium, logout } = useSpotifyAuth();
const { setMode } = useGameState();

function continueWithoutSpotify() {
  setMode('preview');
  router.push('/game');
}

function startGame() {
  // Modus basierend auf Auth-Status setzen
  if (isAuthenticated.value && isPremium.value) {
    setMode('premium');
  } else {
    setMode('preview');
  }
  router.push('/game');
}

function handleLogout() {
  logout();
  setMode('none');
}
</script>

<style scoped lang="scss">
.home-view {
  @apply min-h-screen flex items-center justify-center;
  @apply px-4 py-20;
  background: linear-gradient(135deg, $background-base 0%, $background-dark 100%);
}

.content-container {
  @apply w-full max-w-md;
  @apply flex flex-col gap-8;
}

.hero-section {
  @apply text-center;
}

.app-title {
  @apply text-5xl font-bold m-0 mb-2;
  background: linear-gradient(135deg, $spotify-green, lighten($spotify-green, 20%));
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  text-shadow: 0 0 30px rgba($spotify-green, 0.3);
}

.app-subtitle {
  @apply text-gray-400 text-lg m-0;
}

.status-section {
  @apply flex justify-center;
}

.actions-section {
  @apply flex flex-col gap-4;
}

.info-box {
  @apply bg-gray-900 bg-opacity-50 rounded-2xl p-6;
  @apply border border-gray-800;
  backdrop-filter: blur(10px);
  
  h3 {
    @apply text-white text-lg font-semibold m-0 mb-4;
  }
  
  ol {
    @apply text-gray-300 text-sm;
    @apply pl-5 m-0 mb-4;
    
    li {
      @apply mb-2;
    }
  }
}

.info-note {
  @apply text-gray-500 text-xs m-0;
  @apply pt-4 border-t border-gray-800;
}
</style>